import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Yup from 'yup';
import { FaPlus } from 'react-icons/fa';
import { IoIosWarning } from 'react-icons/io';
import { BiImage, BiMenu, BiTrash, BiX } from 'react-icons/bi';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import Button from '../../../../../../newComponents/Button';
import Input from '../../../../../../newComponents/UnformInput';
import ControlledInput from '../../../../../../newComponents/Input';
import Select from '../../../../../../newComponents/UnformSelect';
import FileInput from '../../../../../../newComponents/FileInput';
import { ModalHandles } from '../../../../../../newComponents/Modal';
import IncrementDecrement from '../../../../../../newComponents/IncrementDecrement';
import Toggle from '../../../../../../newComponents/Toggle';

import {
  ComplementItem as IComplementItem,
  useComplements,
} from '../../../../../../hooks/complements';
import { useAuth } from '../../../../../../hooks/auth';
import { useToast } from '../../../../../../hooks/toast';

import ProductsService from '../../../../../../services/ProductsService';
import ComplementItemsService from '../../../../../../services/ComplementItemsService';

import APIError from '../../../../../../errors/APIError';

import {
  Container,
  ComplementItem,
  ComplementItemContent,
  FileInputContainer,
  DeleteButton,
  ComplementItemsList,
  Modal,
  ModalHeader,
} from './styles';
import { ConfirmUpdateAllModal } from './ConfirmUpdateAllModal';

export const ComplementItemsFormSchema = Yup.object().shape({
  items: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .max(40, 'No máximo 40 caracteres')
        .required('O nome é obrigatório'),
      code: Yup.string().max(40, 'No máximo 20 caracteres'),
    }),
  ),
});

const ComplementItems: React.FC = () => {
  const modalRef = useRef<ModalHandles>(null);

  const { user } = useAuth();
  const { addToast } = useToast();

  const [complementItemIdToRemove, setComplementItemIdToRemove] = useState<
    number | string
  >();
  const [
    isOpenConfirmUpdateAllModal,
    setIsOpenConfirmUpdateAllModal,
  ] = useState(false);
  const [
    selectedComplementItem,
    setSelectedComplementItem,
  ] = useState<IComplementItem | null>(null);

  const {
    items,
    handleAddComplementItem,
    handleChangeComplement,
    handleChangeComplementItem,
    handleRemoveComplementItem: handleSoftRemoveComplementItem,
  } = useComplements();

  const [rawMaterials, setRawMaterials] = useState<
    { value: number; label: string }[]
  >([]);

  const handleTryRemoveComplementItem = useCallback((id: number | string) => {
    modalRef.current?.open();

    setComplementItemIdToRemove(id);
  }, []);

  const handleRemoveComplementItem = useCallback(async () => {
    if (complementItemIdToRemove) {
      handleSoftRemoveComplementItem(complementItemIdToRemove);

      modalRef.current?.close();

      if (typeof complementItemIdToRemove === 'number') {
        try {
          await ComplementItemsService.deleteComplementItem(
            user.empresa_id,
            complementItemIdToRemove,
          );
        } catch (error) {
          if (error instanceof APIError) {
            addToast({
              title: error.message,
              type: 'error',
            });

            return;
          }

          addToast({
            title: 'Ocorreu um erro durante a operação',
            type: 'error',
          });
        }
      }
    }
  }, [
    user.empresa_id,
    complementItemIdToRemove,
    addToast,
    handleSoftRemoveComplementItem,
  ]);

  const handleOnDragEnd = useCallback(
    (dropResult: DropResult) => {
      const { destination, source } = dropResult;

      if (
        typeof destination?.index === 'number' &&
        source.index !== destination.index
      ) {
        let sortedComplementItems = Array.from(items || []);
        const [removedComplementItem] = sortedComplementItems.splice(
          source.index,
          1,
        );

        sortedComplementItems.splice(
          destination.index,
          0,
          removedComplementItem,
        );

        sortedComplementItems = sortedComplementItems.map(
          (complementItem, complementItemIndex) => ({
            ...complementItem,
            order: complementItemIndex + 1,
          }),
        );

        handleChangeComplement({ items: sortedComplementItems });
      }
    },
    [items, handleChangeComplement],
  );

  const handleLoadRawMaterials = useCallback(async () => {
    try {
      const rawMaterialsList = await ProductsService.listRawMaterials(
        user.empresa_id,
      );

      setRawMaterials(
        rawMaterialsList.map(({ id, name }) => ({ value: id, label: name })),
      );
    } catch (error) {
      if (error instanceof APIError) {
        addToast({
          title: error.message,
          type: 'error',
        });
      }
    }
  }, [user.empresa_id, addToast]);

  function handleDefaultValueRawMaterial(
    rawMaterialId?: number,
  ): { value: number; label: string } | undefined {
    const rawMaterial = rawMaterials.find(
      ({ value }) => value === rawMaterialId,
    );

    return rawMaterial;
  }

  useEffect(() => {
    handleLoadRawMaterials();
  }, [handleLoadRawMaterials]);

  const complementItems = items || [];

  const handleOpenConfirmUpdateAllModal = useCallback(() => {
    setIsOpenConfirmUpdateAllModal(true);
  }, []);

  const handleCloseConfirmUpdateAllModal = useCallback(() => {
    setIsOpenConfirmUpdateAllModal(false);
  }, []);

  return (
    <Container>
      <DragDropContext onDragEnd={(dropResult) => handleOnDragEnd(dropResult)}>
        <Droppable droppableId="droppableComplementItems">
          {(droppableProvided) => (
            <ComplementItemsList
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
              {complementItems
                .sort((a, b) => (a?.order ?? 1) - (b?.order ?? 1))
                .map((complementItem, complementItemIndex) => (
                  <Draggable
                    key={String(`complement-item-${complementItem.id}`)}
                    draggableId={String(`complement-item-${complementItem.id}`)}
                    index={complementItemIndex}
                  >
                    {(draggableProvided) => (
                      <ComplementItem
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                        style={{
                          ...draggableProvided.draggableProps.style,
                        }}
                      >
                        <ComplementItemContent>
                          <div {...draggableProvided.dragHandleProps}>
                            <BiMenu size={24} />
                          </div>
                          <div className="left">
                            <FileInputContainer>
                              <FileInput
                                id={`complementItemImage-${complementItem.id}`}
                                icon={BiImage}
                                iconSize={40}
                                selectedImage={complementItem.image}
                                acceptableTypes=".jpeg,.jpg,.png"
                                onRemove={() =>
                                  handleChangeComplementItem({
                                    id: complementItem.id,
                                    image: undefined,
                                  })
                                }
                                onSelectFile={(selectedFile) =>
                                  handleChangeComplementItem({
                                    id: complementItem.id,
                                    image: selectedFile,
                                  })
                                }
                              />
                            </FileInputContainer>
                            <Toggle
                              checked={!complementItem.pauseSale}
                              firstText="Pausado"
                              secondText="Ativado"
                              style={{ marginTop: 6 }}
                              onToggle={(value) => {
                                if (complementItem.code) {
                                  setSelectedComplementItem(complementItem);
                                  handleOpenConfirmUpdateAllModal();
                                } else {
                                  handleChangeComplementItem({
                                    id: complementItem.id,
                                    pauseSale: !value,
                                    updateAll: false,
                                  });
                                }
                              }}
                            />
                            <Toggle
                              checked={Boolean(complementItem.checked)}
                              firstText="Desmarcado"
                              secondText="Marcado"
                              style={{ marginTop: 6 }}
                              onToggle={(value) =>
                                handleChangeComplementItem({
                                  id: complementItem.id,
                                  checked: value,
                                })
                              }
                            />
                          </div>
                          <div className="vertical">
                            <div className="horizontal">
                              <Input
                                name={`items[${complementItemIndex}].name`}
                                label="Nome"
                                placeholder="Ex.: Embalagem"
                              />
                              <Select
                                name={`items[${complementItemIndex}].rawMaterialId`}
                                label="Matéria prima"
                                options={rawMaterials}
                                defaultValue={handleDefaultValueRawMaterial(
                                  complementItem.rawMaterialId,
                                )}
                              />
                              <Input
                                name={`items[${complementItemIndex}].code`}
                                label="Código PDV"
                                placeholder="Ex.: 632"
                              />
                            </div>
                            <div className="horizontal">
                              <ControlledInput
                                name={`items[${complementItemIndex}].price`}
                                label="Preço"
                                value={(
                                  Number(
                                    parseFloat(
                                      String(complementItem.price ?? 0),
                                    )
                                      .toFixed(2)
                                      .replace(/\D/g, ''),
                                  ) / 100
                                ).toLocaleString('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                })}
                                onChange={(event) => {
                                  handleChangeComplementItem({
                                    id: complementItem.id,
                                    price:
                                      Number(
                                        String(event.target.value).replace(
                                          /\D/g,
                                          '',
                                        ),
                                      ) / 100,
                                  });
                                }}
                              />
                              <div className="horizontal min-max">
                                <IncrementDecrement
                                  id="min"
                                  label="Mínimo"
                                  value={complementItem?.minimumQuantity ?? 0}
                                  min={0}
                                  max={complementItem?.maximumQuantity ?? 0}
                                  onChangeValue={(value) =>
                                    handleChangeComplementItem({
                                      id: complementItem.id,
                                      minimumQuantity: value,
                                    })
                                  }
                                />
                                <IncrementDecrement
                                  id="max"
                                  label="Máximo"
                                  value={complementItem?.maximumQuantity ?? 1}
                                  min={complementItem?.minimumQuantity ?? 0}
                                  onChangeValue={(value) =>
                                    handleChangeComplementItem({
                                      id: complementItem.id,
                                      maximumQuantity: value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </ComplementItemContent>
                        {complementItems.length > 1 && (
                          <DeleteButton
                            type="button"
                            onClick={() =>
                              handleTryRemoveComplementItem(complementItem.id)
                            }
                          >
                            <BiTrash size={24} />
                          </DeleteButton>
                        )}
                      </ComplementItem>
                    )}
                  </Draggable>
                ))}
              {droppableProvided.placeholder}
              <Button
                onClick={handleAddComplementItem}
                type="button"
                style={{ height: '50px' }}
              >
                <FaPlus />
                Adicionar Complemento
              </Button>
            </ComplementItemsList>
          )}
        </Droppable>
      </DragDropContext>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Você vai excluir este item complemento.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={() => handleRemoveComplementItem()}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
      {isOpenConfirmUpdateAllModal && selectedComplementItem && (
        <ConfirmUpdateAllModal
          isOpen={isOpenConfirmUpdateAllModal}
          handleClose={handleCloseConfirmUpdateAllModal}
          handleChangeComplementItem={handleChangeComplementItem}
          complementItem={selectedComplementItem}
        />
      )}
    </Container>
  );
};

export default ComplementItems;
